import { useEffect, useRef, useState, useCallback, useMemo } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { ChatMessage } from '../hooks/useSocket';
import type { ToolActivityMessage } from '@voice-assistant/shared';
import { MarkdownContent } from '../shared-ui/MarkdownContent';
import { SessionDetails } from './SessionDetails';
import { humanizeSystemEvent } from '../utils/humanizeSystemEvent';
import { isAnthropicAuthCard, ANTHROPIC_AUTH_CARD_COPY } from '../utils/providerErrorCard';
import { ErrorCard, isErrorMessage } from './ErrorCard';
import { ToolTrace } from '../shared-ui/ToolTrace';
import { ContextResetDivider } from '../shared-ui/ContextResetDivider';
import { HandoffDivider } from './HandoffDivider';
import { TurnMetaDrawer } from './TurnMetaDrawer';
import { AgentQuestionCard } from './AgentQuestionCard';
import { useReaderModeOptional } from '../hooks/useReaderMode';
import { useModels } from '../hooks/useModels';
import { turnModelLabel, modelBadge } from '../utils/modelHelpers';
import { useIsBusy } from '../state/composerStore';
import { shouldSuppressToolRow } from '../state/inflight-reconstruction';
import { useSessionMessages, useSessionHasMore, useSessionLoadState, useSessionLoadStore, retrySession } from '../state/sessionLoadStore';
import { getBrand } from '../brands';
import { DocTtsPlaybackBar } from './DocTtsPlaybackBar';
import { useDocTtsStore, useDocTtsEntry } from '../state/docTtsStore';
import { useSettingsStore } from '../state/settings.store';
import { useTtsShared } from '../hooks/useTtsContext';
/** Beyond this distance from bottom, “follow latest” turns off (no auto-scroll). */
const FOLLOW_BOTTOM_THRESHOLD_PX = 96;

/**
 * #1433 r4 (operator): hide the in-chat navigation arrows — the scroll-to-bottom ↓ and
 * the turn-nav ↑↓ — from the control row. Flip to `true` to bring them back. A plain
 * const (pure presentation choice), not a profile flag.
 */
const CHAT_NAV_ARROWS_ENABLED = false;

/** Format an ISO timestamp as a short time string (HH:MM) or with date if not today */
function formatMsgTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const hhmm = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  if (d.toDateString() === now.toDateString()) return hhmm;
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yest ${hhmm}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${hhmm}`;
}

/** Map a model ID + provider to a human-readable consultation label. */
function consultationDisplayLabel(model?: string, provider?: string): string {
  const m = model || '';
  if (/claude-opus-4-7/.test(m)) return 'Opus 4.7';
  if (/claude-opus-4-6/.test(m)) return 'Opus 4.6';
  if (/claude-opus/.test(m)) {
    const ver = m.match(/claude-opus-(\d+[-.]?\d*)/)?.[1]?.replace('-', '.');
    return ver ? `Opus ${ver}` : 'Opus';
  }
  if (/claude-sonnet-4-6/.test(m)) return 'Sonnet 4.6';
  if (/claude-sonnet-4-5/.test(m)) return 'Sonnet 4.5';
  if (/claude-sonnet/.test(m)) {
    const ver = m.match(/claude-sonnet-(\d+[-.]?\d*)/)?.[1]?.replace('-', '.');
    return ver ? `Sonnet ${ver}` : 'Sonnet';
  }
  if (/claude-haiku-4-5/.test(m)) return 'Haiku 4.5';
  if (/claude-haiku/.test(m)) {
    const ver = m.match(/claude-haiku-(\d+[-.]?\d*)/)?.[1]?.replace('-', '.');
    return ver ? `Haiku ${ver}` : 'Haiku';
  }
  if (/gemini-3\.1-pro|gemini-3/.test(m)) return 'Gemini 3 Pro';
  if (/gemini-2\.5-pro/.test(m)) return 'Gemini 2.5 Pro';
  if (/gemini-2\.5-flash-lite/.test(m)) return 'Gemini Flash-Lite';
  if (/gemini-2\.5-flash/.test(m)) return 'Gemini 2.5 Flash';
  if (/gemini-2\.0-flash/.test(m)) return 'Gemini Flash';
  if (/gemini/.test(m)) return 'Gemini';
  if (/gemma/.test(m)) return m.split(':')[0];
  // Fall back to provider
  const p = provider || '';
  if (p === 'opus') return 'Opus';
  if (p === 'gemini') return 'Gemini';
  if (p === 'haiku') return 'Haiku';
  if (p === 'ollama') return 'Ollama';
  return 'Consultation';
}

/** Format token count compactly: 1234 → "1.2K", 12345 → "12K" */
function fmtTokens(n?: number): string {
  if (n == null) return '';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return String(n);
}

/** Format cost compactly */
function fmtCost(c?: number | string): string {
  const v = Number(c);
  if (!v) return '';
  if (v < 0.001) return `$${v.toFixed(4)}`;
  if (v < 0.01) return `$${v.toFixed(3)}`;
  return `$${v.toFixed(2)}`;
}

/** Message closest to the vertical center of the chat viewport — drives docked play / memo. */
export interface ViewportMessageFocus {
  messageIndex: number;
  role: 'user' | 'assistant' | string;
  text: string;
  canMemo: boolean;
  memoPairUserText: string | null;
  memoPairAssistantText: string | null;
}

function messageTailKey(m: ChatMessage | undefined): string {
  if (!m) return '';
  return `${m.role}:${m.streaming ? 1 : 0}:${m.text?.length ?? 0}`;
}

/** Compact pill for system events — hides AI complexity from non-technical users. */
function SystemPill({ event }: { event: { icon: string; label: string; detail: string; category: string } }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div class={`system-pill system-pill--${event.category}`} onClick={() => setExpanded(!expanded)}>
      <div class="system-pill-header">
        <span class="system-pill-icon">{event.icon}</span>
        <span class="system-pill-label">{event.label}</span>
        <span class={`system-pill-chevron${expanded ? ' system-pill-chevron--open' : ''}`}>›</span>
      </div>
      {expanded && <div class="system-pill-detail">{event.detail}</div>}
    </div>
  );
}

interface Props {
  speaking: boolean;
  /** Which manual TTS source is active; null during auto-speak stream. */
  speakingSourceKey: string | null;
  sessionId?: string;
  onLoadOlder?: () => void;
  onSpeakText: (text: string, sourceKey: string) => void;
  onStopSpeaking: () => void;
  /** When false, play buttons are disabled (master or per-chat sound off). Stop still works. */
  canPlayTts?: boolean;
  /** Fired when the message nearest the viewport center changes (Chat tab only). */
  onViewportFocusChange?: (focus: ViewportMessageFocus | null) => void;
  /** Controlled tab state — which subpanel is visible (chat/details). Lifted from parent. */
  activeTab?: 'chat' | 'details';
  /** Callback when ChatArea wants to reset tab (e.g., session switch). */
  onTabChange?: (tab: 'chat' | 'details') => void;
  /** When true, messages show checkboxes for Opus context selection. */
  selectionMode?: boolean;
  /** Set of message IDs currently selected for Opus. */
  selectedMessageIds?: Set<string>;
  /** Called when user taps a message to toggle selection. */
  onToggleMessageSelection?: (id: string) => void;
  /** First letter of the authenticated user's name — shown as badge on user messages. */
  userInitial?: string;
  /** Fired when follow-latest (at-bottom) state changes — used by playback button to switch mode. */
  onFollowLatestChange?: (following: boolean) => void;
  /** Tap share on a user message → immediately share that Q&A pair. */
  onShareSingle?: (msgIdx: number) => void;
  /** Long-press share on a user message → enter multi-select share mode. */
  onEnterShareSelection?: (msgIdx: number) => void;
  /** Share/download a single media attachment (image). */
  onShareMedia?: (src: string) => void;
  /** Live tool activities during an active query — shown as inline live ToolTrace */
  toolActivities?: (ToolActivityMessage & { turnIndex: number })[];
  /**
   * The live turn's PERSISTED tool row (id from the server). Its content has been seeded into
   * `toolActivities` above, which keeps growing — so the snapshot row is suppressed to avoid
   * rendering the same turn twice. Matched BY ID, never by position: before the first flush the
   * last tool row is the PREVIOUS turn's, and hiding that would delete completed history.
   */
  inflightToolRowId?: string;
  /** When provided, a back-to-sessions button is rendered as part of the nav cluster. */
  onBack?: () => void;
  /** True when viewing an autonomous session (read-only, no input bar) */
  isAutonomous?: boolean;
  /** Label for the autonomous banner (chunk title or plan name) */
  autonomousLabel?: string;
  /** Provider error info — inline banner (Gemini) or guidance card (Anthropic auth, #1057) */
  providerError?: { provider: string; message: string; offerFallback: boolean; authRequired?: boolean; remedy?: 'claude_login' } | null;
  /** Called when user wants to retry the last message with a specific provider */
  onRetryWithProvider?: (text: string, provider: string) => void;
  /** Called when user dismisses the provider error banner */
  onDismissProviderError?: () => void;
  /** Opens the in-app SSH terminal to the user's sandbox (Anthropic auth card remedy). Undefined when SSH terminal is unavailable. */
  onOpenSshTerminal?: () => void;
  /** Opens the Settings modal (used by ErrorCard auth-failure CTA). */
  onOpenSettings?: () => void;
  /** When true, render the expandable HandoffDivider for `handoff:…` system messages.
   *  Non-admins still receive the handoff injection in their next-prompt preamble
   *  (backend-side, in claude.service.ts effectivePrompt) — only the UI surface is gated. */
  isAdmin?: boolean;
  /** Composer top-bar LEFT zone (P3): the session-dots strip, folded into the
   *  same bar as the nav cluster. Render-prop so App owns the dots data — never
   *  faded (persistent wayfinding, unlike the nav zone which fades on scroll). */
  sessionDots?: ComponentChildren;
  /** P5 — find-in-conversation relocated into the nav cluster. Find-and-jump IS
   *  in-chat navigation (same family as the turn-nav arrows), so the trigger lives
   *  here instead of the top-header action row. `onToggleFind` opens/closes the
   *  find field (which reuses the bottom composer pill, #1433); `findActive`
   *  drives the active visual state of this trigger pill. */
  onToggleFind?: () => void;
  findActive?: boolean;
  /** r7 — persistent playback transport. Relocated out of the dots row into the new
   *  .composer-cluster-row (the floating context+playback+find cluster directly above
   *  the dots row). Render-prop so App owns the PlaybackFab + its handlers. Persistent
   *  (NOT scroll-faded) — a primary, mode-aware transport, not chrome. */
  playbackTransport?: ComponentChildren;
  /** Bottom-nav r7 — the session ContextBar, rendered as the leading member of the
   *  .composer-cluster-row (context + playback + find), which floats above the
   *  session-dots row. Render-prop so App owns the ContextBar + its tuning handlers;
   *  mirrors how `playbackTransport` is threaded. (Was previously the BottomNav leading
   *  slot in r6; moved here so the three chat-scoped controls read as one cluster.) */
  clusterContext?: ComponentChildren;
  /** r11 — chat-scoped icon launchers added to the cluster AFTER find: browser + dev-preview.
   *  Render-props so App owns the handlers (openBrowser / DevPreviewButton store wiring).
   *  Icon-only, borderless — styled to match the context pill (.ccl-icon-btn). */
  clusterBrowser?: ComponentChildren;
  clusterDevPreview?: ComponentChildren;
  /** agent-runs §4 — Agent Runs badge, rendered between dev-preview and ssh. Renders as
   *  null when the session has no runs, so the slot self-collapses. */
  clusterWorkflow?: ComponentChildren;
  /** r17 — SSH connection icon, rendered right of dev-preview. */
  clusterSsh?: ComponentChildren;
  /** r15 — settings gear, rendered between ssh and find. Opens Settings. */
  clusterGear?: ComponentChildren;
  /** Phase 5: when true, suppress the floating chat read-aloud bar — the host shell
   *  renders DocTtsPlaybackBar in the composer pill slot instead (mobile). Desktop
   *  omits this prop and keeps the floating bar until its fast-follow. */
  suppressFloatingReadAloudBar?: boolean;
  /** Phase 5: a chat read is active but its composer-slot bar is collapsed — render a
   *  re-show chip in the control cluster. Load-bearing: the slot bar replaces the input
   *  row AND the mic, so this is how the user brings controls back / frees the input to
   *  type or voice a follow-up while narration continues. */
  readAloudCollapsed?: boolean;
  onShowReadAloudBar?: () => void;
}

export function ChatArea({
  speaking,
  speakingSourceKey,
  sessionId,
  onLoadOlder,
  onSpeakText,
  onStopSpeaking,
  canPlayTts = true,
  onViewportFocusChange,
  activeTab: activeTabProp,
  onTabChange,
  selectionMode = false,
  selectedMessageIds,
  onToggleMessageSelection,
  userInitial = '?',
  onFollowLatestChange,
  onShareSingle,
  onEnterShareSelection,
  onShareMedia,
  toolActivities = [],
  inflightToolRowId,
  onBack,
  isAutonomous = false,
  autonomousLabel,
  providerError,
  onRetryWithProvider,
  onDismissProviderError,
  onOpenSshTerminal,
  onOpenSettings,
  isAdmin = false,
  sessionDots,
  onToggleFind,
  findActive = false,
  playbackTransport,
  clusterContext,
  clusterBrowser,
  clusterDevPreview,
  clusterWorkflow,
  clusterSsh,
  clusterGear,
  suppressFloatingReadAloudBar = false,
  readAloudCollapsed = false,
  onShowReadAloudBar,
}: Props) {
  // Subscribe to sessionLoadStore — single source of truth for messages + hasMore after Phase 3.
  const messages = useSessionMessages(sessionId ?? null);
  const hasMore = useSessionHasMore(sessionId ?? null);
  const loadMode = useSessionLoadState(sessionId ?? null);
  const busy = useIsBusy(sessionId ?? null);
  // Registry = single source of truth for the per-message model badge label (Phase 3/4).
  const { models: registryModels } = useModels();
  const bottomRef = useRef<HTMLDivElement>(null);
  // Bottom-nav r5: clusterRef + useChatAreaFade removed with the control row's
  // .ctb-nav-zone (its only scroll-fade target). The dots+playback row is persistent
  // wayfinding and is deliberately never faded.
  const containerRef = useRef<HTMLDivElement>(null);
  // Long-press timer for share icon — useRef not useState (avoids re-render mid-gesture freeze)
  const shareLongPressRef = useRef<number | null>(null);
  // Track the actual DOM element so the scroll-listener useEffect re-runs when it changes
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const containerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainerEl(el);
  }, []);

  // Register this chat-area container as a Reader Mode surface so the overlay
  // can resolve surfaceId → element for anchoring/text extraction.
  const reader = useReaderModeOptional();
  const readerSurfaceId = `chat-${sessionId || 'new'}`;
  useEffect(() => {
    if (!reader || !containerEl) return;
    return reader.registerSurface(readerSurfaceId, containerEl);
  }, [reader, containerEl, readerSurfaceId]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const onViewportFocusChangeRef = useRef(onViewportFocusChange);
  onViewportFocusChangeRef.current = onViewportFocusChange;
  /** Mirrors focused message index for scroll handler without stale closures */
  const focusIdxRef = useRef(-1);
  const lastFocusPayloadKeyRef = useRef('');
  const layoutRafRef = useRef<number | null>(null);
  const [collapsedAfter, setCollapsedAfter] = useState<Set<number>>(new Set());
  // Turn meta drawer state
  const [turnMetaOpen, setTurnMetaOpen] = useState(false);
  const [turnMetaMessageId, setTurnMetaMessageId] = useState<string | null>(null);
  const [turnMetaAnchorY, setTurnMetaAnchorY] = useState(0);
  const [turnMetaInitial, setTurnMetaInitial] = useState<{ model?: string; costUsd?: number; timestamp?: string }>({});
  const openTurnMeta = useCallback((msg: ChatMessage, e: MouseEvent) => {
    if (!msg.id) return;
    setTurnMetaMessageId(msg.id);
    setTurnMetaAnchorY(e.clientY);
    setTurnMetaInitial({ model: msg.model, costUsd: msg.costUsd, timestamp: msg.timestamp });
    setTurnMetaOpen(true);
  }, []);

  // ── Per-message read-aloud (docTtsStore singleton — "same way" as the doc
  // viewer) ─────────────────────────────────────────────────────────────────
  // The store is a source-keyed singleton; a chatMessage source is
  // { kind:'chatMessage', sessionId, messageId }. The active message (whose id
  // matches source.messageId) gets the four ttsMode props passed to its
  // MarkdownContent so it annotates segments + highlights the playing paragraph.
  const {
    phase: chatTtsPhase,
    source: chatTtsSource,
    currentSegment: chatTtsCurrentSegment,
    unlockAudio: ttsUnlockAudio,
    startReading: ttsStartReading,
    stop: ttsStopReading,
    seekTo: ttsSeekTo,
  } = useDocTtsEntry();
  const setChatTtsSegments = useDocTtsStore((s) => s.setSegments);
  // Reactive selector (not the module-private isBrowserTts()) so the ▶ control
  // reacts to a late profile load. Decision 3: gate the control OFF on browser-TTS
  // profiles (Protofac) — doc read-aloud is already server-only there.
  const ttsProvider = useSettingsStore((s) => s.ttsProvider);
  const isBrowserTtsProfile = ttsProvider === 'browser';
  // The shared TTS instance (chat pill + streaming auto-read). Starting a chat
  // read must stop it so the two channels never overlap (P2 cross-stop, start side).
  const sharedTts = useTtsShared();
  // Active read is a chat-message read for THIS message?
  const isChatReadActiveForMsg = useCallback(
    (msgId: string | undefined): boolean =>
      !!msgId &&
      chatTtsSource?.kind === 'chatMessage' &&
      chatTtsSource.messageId === msgId &&
      chatTtsPhase !== 'idle' &&
      chatTtsPhase !== 'stopped',
    [chatTtsSource, chatTtsPhase],
  );
  // A chat read is active for ANY message in this session (mount the bar).
  const isChatReadActive =
    chatTtsSource?.kind === 'chatMessage' &&
    chatTtsSource.sessionId === (sessionId ?? '') &&
    chatTtsPhase !== 'idle' &&
    chatTtsPhase !== 'stopped';

  const onReadMessageAloud = useCallback(
    (msgId: string) => {
      if (!sessionId) return;
      if (isChatReadActiveForMsg(msgId)) {
        ttsStopReading();
        return;
      }
      // Cross-stop (start side): silence the chat pill / streaming auto-read AND
      // Reader Mode before starting so only one channel speaks. Reader Mode owns a
      // separate instance and listens for a docTtsStore rising-edge (ReaderOverlay).
      // cancelStreamAutoRead() does everything stop() does (gen bump, killAudio,
      // drain queue) PLUS it clears the once-per-response auto-read latch — so a
      // streaming auto-read that was ALREADY latched true mid-stream stops feeding
      // immediately instead of restarting the runner concurrently with this read
      // (the double-audio regression). The runner's continuous gate is the backstop.
      sharedTts.cancelStreamAutoRead();
      // Mobile autoplay: prime the audio element INSIDE the tap gesture, THEN
      // startReading (mirror CodeViewer.handleReadAloud). The first segment plays
      // only after the WS round-trip (outside the gesture) — without this prime,
      // iOS/Android silently reject it.
      ttsUnlockAudio();
      ttsStartReading({ kind: 'chatMessage', sessionId, messageId: msgId });
    },
    [sessionId, isChatReadActiveForMsg, ttsStopReading, sharedTts, ttsUnlockAudio, ttsStartReading],
  ); // sharedTts is the stable context value; cancelStreamAutoRead read off it

  // System-action messages are collapsed by default; user can tap to expand
  const [expandedSysActions, setExpandedSysActions] = useState<Set<number>>(new Set());
  const toggleSysAction = useCallback((i: number) => {
    setExpandedSysActions(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }, []);

  // Last user message text — used by provider error banner retry buttons
  const lastUserText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].text;
    }
    return null;
  }, [messages]);

  // Consultation summary — derived from message list, no API call needed
  const consultationSummary = useMemo(() => {
    const cons = messages.filter(m => m.role === 'consultation');
    if (cons.length === 0) return null;
    const totalCost = cons.reduce((s, m) => s + (Number(m.costUsd) || 0), 0);
    return { count: cons.length, totalCost, items: cons };
  }, [messages]);
  const lastAssistantIdx = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].streaming) return i;
    }
    return -1;
  }, [messages]);
  const [consultDrawerOpen, setConsultDrawerOpen] = useState(false);

  // For each message index, the index of the nearest preceding user message (-1 if none).
  // Used to determine which agent messages collapse when a user bubble is tapped.
  const precedingUserIdx = useMemo(() => {
    const result = new Array<number>(messages.length).fill(-1);
    let lastUser = -1;
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].role === 'user') lastUser = i;
      else result[i] = lastUser;
    }
    return result;
  }, [messages]);

  const toggleCollapse = useCallback((userMsgIndex: number) => {
    setCollapsedAfter(prev => {
      const next = new Set(prev);
      if (next.has(userMsgIndex)) next.delete(userMsgIndex);
      else next.add(userMsgIndex);
      return next;
    });
  }, []);

  // Flash-highlight the source element when user quotes from an assistant message
  useEffect(() => {
    const handler = (e: Event) => {
      const sourceEl = (e as CustomEvent).detail?.sourceEl as Element | undefined;
      if (!sourceEl) return;
      sourceEl.classList.add('quote-source-flash');
      setTimeout(() => sourceEl.classList.remove('quote-source-flash'), 2500);
    };
    document.addEventListener('quote-source', handler);
    return () => document.removeEventListener('quote-source', handler);
  }, []);

  const [loadingOlder, setLoadingOlder] = useState(false);
  const [activeTabInternal, setActiveTabInternal] = useState<'chat' | 'details'>('chat');
  const activeTab = activeTabProp ?? activeTabInternal;
  const setActiveTab = onTabChange ?? setActiveTabInternal;
  
  /** Track previous message count to detect prepended older messages */
  const prevCountRef = useRef(messages.length);
  /** When true, new messages / stream will not auto-scroll the viewport */
  const userScrolledUpRef = useRef(false);
  /** Follow-latest: on = auto-scroll; off = paused (user scrolled up). Always show control. */
  const [followLatest, setFollowLatestRaw] = useState(true);
  const setFollowLatest = useCallback((v: boolean | ((prev: boolean) => boolean)) => {
    setFollowLatestRaw((prev) => {
      const next = typeof v === 'function' ? v(prev) : v;
      if (next !== prev) onFollowLatestChange?.(next);
      return next;
    });
  }, [onFollowLatestChange]);
  const prevTailKeyRef = useRef('');
  /** Message row aligned with viewport center (for soft highlight). */
  const [focusedMessageIndex, setFocusedMessageIndex] = useState(-1);
  /** Prior session id — for switching back to Chat tab */
  const prevSessionRef = useRef(sessionId);
  /** Last session we applied “scroll to bottom” for (separate from tab UI ref) */
  const lastScrollSessionRef = useRef<string | undefined>(undefined);

  // When session switches, reset tab to chat
  useEffect(() => {
    if (prevSessionRef.current !== sessionId) {
      setActiveTab('chat');
      prevSessionRef.current = sessionId;
    }
  }, [sessionId]);

  const updateScrollState = useCallback(() => {
    const el = containerRef.current;
    if (!el || activeTab !== 'chat') return;
    const raw = el.scrollHeight - el.scrollTop - el.clientHeight;
    const distanceFromBottom = Math.max(0, raw);
    const scrolledUp = distanceFromBottom > FOLLOW_BOTTOM_THRESHOLD_PX;
    userScrolledUpRef.current = scrolledUp;
    const nextFollow = !scrolledUp;
    setFollowLatest((prev) => (prev === nextFollow ? prev : nextFollow));
  }, [activeTab]);

  const buildViewportFocus = useCallback((index: number): ViewportMessageFocus | null => {
    const msgs = messagesRef.current;
    const msg = msgs[index];
    if (!msg || msg.role === 'system') return null;
    const text = (msg.text ?? '').trim();
    const prev = index > 0 ? msgs[index - 1] : null;
    const canPair =
      msg.role === 'assistant' &&
      prev?.role === 'user' &&
      !msg.streaming &&
      !!prev.text?.trim() &&
      !!text;
    return {
      messageIndex: index,
      role: msg.role as 'user' | 'assistant',
      text,
      canMemo: canPair,
      memoPairUserText: canPair ? prev!.text : null,
      memoPairAssistantText: canPair ? msg.text : null,
    };
  }, []);

  const recomputeViewportFocus = useCallback(() => {
    const el = containerRef.current;
    const notify = onViewportFocusChangeRef.current;
    const msgs = messagesRef.current;
    if (!el || activeTab !== 'chat') {
      focusIdxRef.current = -1;
      lastFocusPayloadKeyRef.current = '';
      setFocusedMessageIndex(-1);
      notify?.(null);
      return;
    }
    if (msgs.length === 0) {
      focusIdxRef.current = -1;
      lastFocusPayloadKeyRef.current = '';
      setFocusedMessageIndex(-1);
      notify?.(null);
      return;
    }

    // Fast path: during streaming, the last message is always focused (it's growing).
    // Skip expensive DOM querySelectorAll + getBoundingClientRect — just update the payload.
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg?.streaming && focusIdxRef.current === msgs.length - 1) {
      // Index unchanged, just content growing — notify with updated payload but skip DOM scan
      const payloadKey = `${msgs.length - 1}:1:${(lastMsg.text ?? '').length}`;
      lastFocusPayloadKeyRef.current = payloadKey;
      notify?.(buildViewportFocus(msgs.length - 1));
      return;
    }

    const roots = el.querySelectorAll<HTMLElement>('[data-chat-focus-root]');
    if (roots.length === 0) {
      focusIdxRef.current = -1;
      lastFocusPayloadKeyRef.current = '';
      setFocusedMessageIndex(-1);
      notify?.(null);
      return;
    }
    const cr = el.getBoundingClientRect();
    // Reading zone at 22% from top — text here is what plays when ▶ is pressed
    const cy = cr.top + cr.height * 0.22;

    let best = null as HTMLElement | null;
    let bestDist = Infinity;
    roots.forEach((node) => {
      const r = node.getBoundingClientRect();
      if (r.bottom < cr.top || r.top > cr.bottom) return;
      const mid = (r.top + r.bottom) / 2;
      const d = Math.abs(mid - cy);
      if (d < bestDist) {
        bestDist = d;
        best = node;
      }
    });
    const pickByVisibleArea = () => {
      let bestIdx = -1;
      let bestArea = 0;
      roots.forEach((node) => {
        const r = node.getBoundingClientRect();
        const top = Math.max(r.top, cr.top);
        const bottom = Math.min(r.bottom, cr.bottom);
        const h = Math.max(0, bottom - top);
        const area = h * Math.min(r.width, cr.width);
        const idx = parseInt(node.dataset.msgIdx ?? '-1', 10);
        if (area > bestArea) {
          bestArea = area;
          bestIdx = idx;
        }
      });
      return bestIdx;
    };
    let idx = -1;
    if (best) {
      idx = parseInt(best.dataset.msgIdx ?? '-1', 10);
    }
    if (idx < 0) {
      idx = pickByVisibleArea();
    }
    if (idx < 0) {
      focusIdxRef.current = -1;
      lastFocusPayloadKeyRef.current = '';
      setFocusedMessageIndex(-1);
      notify?.(null);
      return;
    }
    const m = msgs[idx];
    const payloadKey = `${idx}:${m?.streaming ? 1 : 0}:${(m?.text ?? '').length}`;
    if (idx === focusIdxRef.current && lastFocusPayloadKeyRef.current === payloadKey) {
      return;
    }
    lastFocusPayloadKeyRef.current = payloadKey;
    if (idx !== focusIdxRef.current) {
      focusIdxRef.current = idx;
      setFocusedMessageIndex(idx);
    }
    notify?.(buildViewportFocus(idx));
  }, [activeTab, buildViewportFocus]);

  const runChatLayout = useCallback(() => {
    updateScrollState();
    recomputeViewportFocus();
  }, [updateScrollState, recomputeViewportFocus]);

  const scheduleChatLayout = useCallback(() => {
    if (layoutRafRef.current != null) return;
    layoutRafRef.current = requestAnimationFrame(() => {
      layoutRafRef.current = null;
      runChatLayout();
    });
  }, [runChatLayout]);

  // One rAF-coalesced pass per frame for scroll + resize (avoids sync layout thrash).
  // Depends on containerEl so it re-attaches when the DOM element changes (e.g. empty→messages).
  useEffect(() => {
    if (activeTab !== 'chat') return;
    const el = containerEl;
    if (!el) return;
    el.addEventListener('scroll', scheduleChatLayout, { passive: true });
    const ro = new ResizeObserver(() => scheduleChatLayout());
    ro.observe(el);
    scheduleChatLayout();
    return () => {
      el.removeEventListener('scroll', scheduleChatLayout);
      ro.disconnect();
      if (layoutRafRef.current != null) {
        cancelAnimationFrame(layoutRafRef.current);
        layoutRafRef.current = null;
      }
    };
  }, [activeTab, scheduleChatLayout, containerEl]);

  useEffect(() => {
    if (activeTab !== 'chat') {
      focusIdxRef.current = -1;
      lastFocusPayloadKeyRef.current = '';
      onViewportFocusChangeRef.current?.(null);
      return;
    }
    scheduleChatLayout();
  }, [messages, activeTab, scheduleChatLayout]);

  /** Re-enable follow + jump to bottom (instant). */
  const resumeFollowLatest = useCallback(() => {
    setActiveTab('chat');
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      userScrolledUpRef.current = false;
      setFollowLatest(true);
      requestAnimationFrame(runChatLayout);
    });
  }, [runChatLayout]);

  /** Jump to the start of the previous turn (the turn whose top is above the viewport). */
  const jumpToPrevTurn = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const turns = Array.from(container.querySelectorAll('[data-chat-focus-root]')) as HTMLElement[];
    if (!turns.length) return;
    const containerTop = container.getBoundingClientRect().top;
    const THRESHOLD = 40;
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].getBoundingClientRect().top - containerTop < -THRESHOLD) {
        turns[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
  }, []);

  /** Jump to the start of the next turn below the current viewport top. */
  const jumpToNextTurn = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const turns = Array.from(container.querySelectorAll('[data-chat-focus-root]')) as HTMLElement[];
    if (!turns.length) return;
    const containerTop = container.getBoundingClientRect().top;
    const THRESHOLD = 40;
    for (let i = 0; i < turns.length; i++) {
      if (turns[i].getBoundingClientRect().top - containerTop > THRESHOLD) {
        turns[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
  }, []);

  // Session change — jump to bottom after layout (messages may arrive next tick)
  useEffect(() => {
    if (!sessionId || lastScrollSessionRef.current === sessionId) return;
    lastScrollSessionRef.current = sessionId;
    userScrolledUpRef.current = false;
    setFollowLatest(true);
    requestAnimationFrame(() => {
      prevCountRef.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      requestAnimationFrame(() => {
        prevCountRef.current = messages.length;
        bottomRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
        runChatLayout();
      });
    });
  }, [sessionId, runChatLayout]);

  // Scroll to last user message when 'va:scroll-to-last-prompt' event fires.
  // Uses a pending ref so the scroll runs after messages have loaded into the DOM.
  const pendingScrollToLastPromptRef = useRef(false);
  useEffect(() => {
    const handler = () => { pendingScrollToLastPromptRef.current = true; };
    window.addEventListener('va:scroll-to-last-prompt', handler);
    return () => window.removeEventListener('va:scroll-to-last-prompt', handler);
  }, []);
  useEffect(() => {
    if (!pendingScrollToLastPromptRef.current || !messages.length) return;
    pendingScrollToLastPromptRef.current = false;
    // Double rAF: first frame commits DOM from session switch, second frame measures/scrolls.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;
      const turns = Array.from(container.querySelectorAll('[data-chat-focus-root]')) as HTMLElement[];
      for (let i = turns.length - 1; i >= 0; i--) {
        if (turns[i].classList.contains('user')) {
          turns[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    }));
  }, [messages]);

  // Auto-scroll when follow-latest is on (not when user scrolled up / paused)
  useEffect(() => {
    const el = containerRef.current;
    const prevLen = prevCountRef.current;
    const newLen = messages.length;
    const last = messages[newLen - 1];
    const streamingTail = last?.role === 'assistant' && !!last?.streaming;

    const scrollBottom = (behavior: ScrollBehavior) => {
      // Single rAF — scheduleChatLayout already deduplicates via layoutRafRef
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior });
        scheduleChatLayout();
      });
    };

    const syncRefs = () => {
      prevCountRef.current = newLen;
      prevTailKeyRef.current = messageTailKey(last);
    };

    if (userScrolledUpRef.current) {
      syncRefs();
      return;
    }

    if (el && newLen > prevLen) {
      const prevTail = prevTailKeyRef.current;
      const newLastKey = messageTailKey(last);
      const prepended = prevLen > 0 && newLastKey === prevTail;
      syncRefs();
      if (!prepended) {
        // Use 'instant' when the user was already near the bottom so the new
        // message is immediately visible before streaming text can appear.
        // Use 'smooth' only when the user was scrolled far up — so they see
        // where they jumped to. Threshold: within 120px of bottom.
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        scrollBottom(nearBottom ? 'instant' : 'smooth');
      }
      return;
    }

    if (el && streamingTail) {
      // During streaming, just scroll + schedule layout (no nested rAFs)
      scrollBottom('auto');
      syncRefs();
      return;
    }

    if (newLen !== prevLen) {
      syncRefs();
      scrollBottom('instant');
    }
  }, [messages, scheduleChatLayout]);

  // Reset loading state when messages change (older messages arrived)
  useEffect(() => {
    setLoadingOlder(false);
  }, [messages.length]);

  const handleLoadOlder = useCallback(() => {
    if (onLoadOlder && hasMore && !loadingOlder) {
      setLoadingOlder(true);
      onLoadOlder();
    }
  }, [onLoadOlder, hasMore, loadingOlder]);

  if (messages.length === 0) {
    // Phase 4: show loading skeleton instead of empty hint during initial fetch.
    if (loadMode === 'fetching_initial') {
      return (
        <div class="chat-area chat-area--loading" ref={containerCallbackRef}>
          <div class="messages-loading-skeleton">
            {[80, 60, 90, 50, 70].map((width, i) => (
              <div key={i} class={`skeleton-line skeleton-line--${i % 2 === 0 ? 'left' : 'right'}`} style={{ width: `${width}%` }} />
            ))}
          </div>
          <div ref={bottomRef} />
        </div>
      );
    }
    // Phase 5 bug fix: show error state with retry button when openSession()
    // timed out (e.g. session limit reached and backend rejected silently —
    // previously this left the user staring at an infinite skeleton).
    if (loadMode === 'error' && sessionId) {
      const lastError = useSessionLoadStore.getState().sessions.get(sessionId)?.lastError;
      return (
        <div class="chat-area chat-area--error" ref={containerCallbackRef}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center', gap: '12px' }}>
            <div style={{ color: 'var(--text-dim)', fontSize: '14px', maxWidth: '280px' }}>
              {lastError || 'Could not load messages.'}
            </div>
            <button
              type="button"
              onClick={() => retrySession(sessionId)}
              style={{
                padding: '8px 16px',
                background: 'var(--theme-accent)',
                color: 'var(--accent-fg)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                minHeight: '44px',
              }}
            >
              Retry
            </button>
          </div>
          <div ref={bottomRef} />
        </div>
      );
    }
    return (
      <div class="chat-area" ref={containerCallbackRef}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-dim)',
            fontSize: '14px',
            textAlign: 'center',
            padding: '20px',
          }}
        >
          {getBrand().voiceOnlyMode ? (
            <>
              Hold the mic button and speak.
              <br />
              Or tap the paperclip to attach an image.
            </>
          ) : (
            <>
              Tap ✎ to start a message.
              <br />
              Drop an image any time.
            </>
          )}
        </div>
        <div ref={bottomRef} />
      </div>
    );
  }

  return (
    <div class="chat-area-container">

      {activeTab === 'details' && (
        <SessionDetails
          messages={messages}
          speaking={speaking}
          speakingSourceKey={speakingSourceKey}
          onSpeakText={onSpeakText}
          onStopSpeaking={onStopSpeaking}
          canPlayTts={canPlayTts}
        />
      )}

      <div
        class="chat-scroll-shell"
        style={{ display: activeTab === 'chat' ? 'flex' : 'none', flex: 1, minHeight: 0, minWidth: 0, flexDirection: 'column' }}
      >
      {isAutonomous && (
        <div class="chat-autonomous-banner">
          <span class="auto-banner-icon">&#9881;</span>
          <span class="auto-banner-label">
            Autonomous Agent {autonomousLabel ? <> — <span class="auto-banner-chunk">{autonomousLabel}</span></> : null}
          </span>
          <span style={{ fontSize: '11px', opacity: 0.7 }}>read-only</span>
        </div>
      )}
      <div class="chat-area" ref={containerCallbackRef}>
      {hasMore && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <button
            class="load-older-btn"
            onClick={handleLoadOlder}
            disabled={loadingOlder}
          >
            {loadingOlder ? 'Loading...' : 'Load older messages'}
          </button>
        </div>
      )}
      {messages.map((msg, i) => {
        if (msg.role === 'system') {
          // Handoff boundary — rendered as an expandable divider with structured content.
          // Admin-only UI; non-admins still receive the injection in their next prompt
          // (backend-side, claude.service.ts effectivePrompt) but don't see the card.
          if (msg.text?.startsWith('handoff:')) {
            if (!isAdmin) return null;
            return (
              <HandoffDivider
                key={i}
                content={msg.text.replace(/^handoff:/, '')}
                consumedAt={msg.metadata?.handoffConsumedAt}
                status={msg.metadata?.handoffStatus}
              />
            );
          }
          // Context reset boundary — rendered as a special divider, not a generic pill
          if (msg.text?.startsWith('context-reset:')) {
            const isoTimestamp = msg.text.slice('context-reset:'.length);
            return <ContextResetDivider key={i} isoTimestamp={isoTimestamp} />;
          }
          // Always humanize known patterns → SystemPill, regardless of compact setting.
          // This ensures auto-triggered events (e.g. /docs-update quick) never look
          // like commands the operator typed. Only truly unrecognized text falls back
          // to the raw session-divider.
          const humanized = humanizeSystemEvent(msg.text);
          if (humanized) {
            return <SystemPill key={i} event={humanized} />;
          }
          return (
            <div key={i} class="session-divider">
              <span class="session-divider-line" />
              <span class="session-divider-text">{msg.text}</span>
              <span class="session-divider-line" />
            </div>
          );
        }
        if (msg.role === 'agent_question' && msg.questions) {
          return (
            <AgentQuestionCard
              key={i}
              questions={msg.questions}
              onAnswer={(answer) => {
                window.dispatchEvent(new CustomEvent('agent-question-answer', { detail: { answer, sessionId } }));
              }}
            />
          );
        }
        if (msg.role === 'system-action' || msg.role === 'shared') {
          const isExpanded = expandedSysActions.has(i);
          const isShared = msg.role === 'shared';
          const preview = (msg.text ?? '').replace(/\n/g, ' ').slice(0, 72);
          const hasMore = (msg.text ?? '').length > 72 || (msg.text ?? '').includes('\n');
          return (
            <div key={i} class={`message system-action-msg${isShared ? ' shared-msg' : ''}${isExpanded ? ' expanded' : ''}`}
              onClick={() => toggleSysAction(i)}
              role="button"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              <div class="system-action-header">
                <span class="system-action-label">{isShared ? '↗ shared' : '⚙ sys'}</span>
                <span class="system-action-preview">{isExpanded ? '' : preview}{!isExpanded && hasMore ? '…' : ''}</span>
                <span class="system-action-chevron">{isExpanded ? '▴' : '▾'}</span>
                {msg.timestamp && (
                  <span class="msg-timestamp system-action">{formatMsgTime(msg.timestamp)}</span>
                )}
              </div>
              {isExpanded && (
                <div class="system-action-content">{msg.text}</div>
              )}
            </div>
          );
        }
        if (msg.role === 'tool') {
          // Hide this row ONLY while the live trace (seeded from it, still growing) is actually
          // on screen — i.e. only while `busy`, the very predicate that renders the live trace
          // below. A suppression that can outlive the live trace makes the turn's whole tool
          // history disappear at the completion boundary. See shouldSuppressToolRow. SE-10.
          if (shouldSuppressToolRow(msg.id, inflightToolRowId, busy)) return null;
          return <ToolTrace key={i} text={msg.text} />;
        }
        if (msg.role === 'consultation-injection' || msg.role === 'cascade-classifier' || msg.role === 'cascade-tag') {
          // Internal system calls — never shown in the chat UI
          return null;
        }
        if (msg.role === 'consultation') {
          const providerFromField = msg.provider;
          const providerFromText = (msg.text || '').startsWith('🧠') ? 'opus'
            : (msg.text || '').startsWith('🔮') ? 'gemini'
            : (msg.text || '').startsWith('⚡') ? 'haiku'
            : (msg.text || '').startsWith('🦙') ? 'ollama'
            : 'opus';
          const provider = providerFromField || providerFromText;
          const providerEmoji = provider === 'opus' ? '🧠' : provider === 'gemini' ? '🔮' : provider === 'haiku' ? '⚡' : '🦙';
          const displayLabel = consultationDisplayLabel(msg.model, provider);
          // Strip the leading emoji tag line (e.g. "🧠 *[Opus consultation]*\n\n")
          const bodyText = (msg.text || '').replace(/^[🧠🔮⚡🦙]\s*\*\[.*?\]\*\n\n/, '');
          const inTok = fmtTokens(msg.inputTokens);
          const outTok = fmtTokens(msg.outputTokens);
          const costStr = fmtCost(msg.costUsd);
          const hasMeta = inTok || outTok || costStr;
          return (
            <div key={i} class={`message consultation consultation--${provider}`}>
              <div class="consultation-header">
                <span class="consultation-icon">{providerEmoji}</span>
                <span class="consultation-label">{displayLabel} Consultation</span>
                {hasMeta && (
                  <span class="consultation-meta-pill" title={msg.model || provider}>
                    {[inTok && `${inTok} in`, outTok && `${outTok} out`, costStr].filter(Boolean).join(' · ')}
                  </span>
                )}
                {msg.timestamp && (
                  <span class="msg-timestamp consultation-time">{formatMsgTime(msg.timestamp)}</span>
                )}
              </div>
              <div class="consultation-body">
                <MarkdownContent text={bodyText} streaming={!!msg.streaming} />
              </div>
            </div>
          );
        }
        if (msg.role === 'error') {
          const isExpanded = expandedSysActions.has(i);
          const preview = (msg.text ?? '').replace(/\n/g, ' ').slice(0, 80);
          const hasMore = (msg.text ?? '').length > 80 || (msg.text ?? '').includes('\n');
          return (
            <div
              key={i}
              class={`message error-pill${isExpanded ? ' expanded' : ''}`}
              onClick={() => toggleSysAction(i)}
              role="button"
              title={isExpanded ? 'Collapse' : 'Expand error detail'}
            >
              <div class="system-action-header">
                <span class="system-action-label error-pill-label">⚠ error</span>
                <span class="system-action-preview">{isExpanded ? '' : preview}{!isExpanded && hasMore ? '…' : ''}</span>
                <span class="system-action-chevron">{isExpanded ? '▴' : '▾'}</span>
              </div>
              {isExpanded && (
                <div class="system-action-content error-pill-content">{msg.text}</div>
              )}
            </div>
          );
        }
        if (msg.role === 'assistant' && isErrorMessage(msg.text || '', msg.metadata)) {
          return (
            <div key={i} class="message message-error" aria-live="polite">
              <ErrorCard
                text={msg.text || ''}
                metadata={msg.metadata}
                onOpenSettings={onOpenSettings}
                onSwitchToGemini={onRetryWithProvider && lastUserText
                  ? () => onRetryWithProvider!(lastUserText!, 'google')
                  : undefined}
              />
            </div>
          );
        }
        const isFocusable = msg.role === 'user' || msg.role === 'assistant';
        const isFocused = isFocusable && focusedMessageIndex === i;
        const focusProps = isFocusable
          ? { 'data-chat-focus-root': '1' as const, 'data-msg-idx': String(i) }
          : {};
        const isSelectable = selectionMode && isFocusable && !!msg.id;
        const isSelected = isSelectable && !!msg.id && selectedMessageIds?.has(msg.id);

        // Collapse: non-user messages hide when their owning user bubble is collapsed
        const isCollapsed = msg.role !== 'user' && precedingUserIdx[i] >= 0 && collapsedAfter.has(precedingUserIdx[i]);
        const isUserCollapsed = msg.role === 'user' && collapsedAfter.has(i);

        return (
          <div
            key={i}
            class={`message ${msg.role}${msg.role === 'user' ? ' transcription' : ''}${
              isFocused ? ' message--viewport-focused' : ''
            }${isSelectable ? ' msg-selectable' : ''}${isSelected ? ' msg-selected' : ''}${
              isCollapsed ? ' message--collapsed' : ''
            }${isUserCollapsed ? ' message--user-collapsed' : ''}`}
            data-reader-block-id={msg.id || `idx-${i}`}
            {...focusProps}
            onClick={
              msg.role === 'user' && !selectionMode
                ? () => toggleCollapse(i)
                : isSelectable && msg.id
                ? () => onToggleMessageSelection?.(msg.id!)
                : undefined
            }
          >
            {isSelectable && (
              <span class={`msg-select-check${isSelected ? ' msg-select-check--on' : ''}`} aria-hidden="true">
                {isSelected ? '✓' : '○'}
              </span>
            )}
            {/* Share icon — left of user bubble, tap=share one Q&A, long-press=select mode */}
            {msg.role === 'user' && !selectionMode && !msg.streaming && (onShareSingle || onEnterShareSelection) && (
              <button
                class="msg-share-btn"
                aria-label="Share"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  shareLongPressRef.current = window.setTimeout(() => {
                    shareLongPressRef.current = null;
                    onEnterShareSelection?.(i);
                  }, 500);
                }}
                onPointerUp={(e) => {
                  e.stopPropagation();
                  if (shareLongPressRef.current !== null) {
                    clearTimeout(shareLongPressRef.current);
                    shareLongPressRef.current = null;
                    onShareSingle?.(i);
                  }
                }}
                onPointerLeave={() => {
                  if (shareLongPressRef.current !== null) {
                    clearTimeout(shareLongPressRef.current);
                    shareLongPressRef.current = null;
                  }
                }}
              >↗</button>
            )}
            {msg.images && msg.images.length > 0 && (
              <div class="message-images">
                {msg.images.map((src, j) => (
                  <div class="media-share-wrapper" key={j}>
                    <img
                      class="message-image-thumb"
                      src={src}
                      alt="attached"
                      onClick={() => window.dispatchEvent(new CustomEvent('open-image-overlay', { detail: src }))}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                    {onShareMedia && (
                      <button
                        class="media-share-btn"
                        title="Share image"
                        onClick={(e) => { e.stopPropagation(); onShareMedia(src); }}
                      >↗</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {msg.role === 'assistant' && msg.model && !msg.streaming && (
              <span class={`model-badge model-badge--full model-badge--top ${modelBadge(msg.model, false).cls}`} title={msg.model} onClick={(e: MouseEvent) => openTurnMeta(msg, e)}>{turnModelLabel(msg.model, registryModels)}</span>
            )}
            <div class={`message-content-row${msg.role === 'assistant' ? ' message-content-row--assistant' : ''}`}>
              <div class="message-content-main">
                {msg.role === 'assistant' ? (
                  isChatReadActiveForMsg(msg.id) ? (
                    /* Active read-aloud target — wire the four ttsMode props so this
                       message annotates segments + highlights the playing paragraph,
                       exactly like the doc viewer (CodeViewer:548-552). */
                    <MarkdownContent
                      text={msg.text}
                      streaming={!!msg.streaming}
                      ttsMode
                      activeTtsSegment={chatTtsCurrentSegment}
                      onTtsSegmentClick={ttsSeekTo}
                      onSegmentsReady={setChatTtsSegments}
                    />
                  ) : (
                    <MarkdownContent text={msg.text} streaming={!!msg.streaming} />
                  )
                ) : msg.text}
                {msg.streaming && (msg.text || '').length < 20 ? (
                  <div class="activity-indicator">
                    <span class="activity-indicator-dots"><span /><span /><span /></span>
                    <span class="activity-indicator-label">Working...</span>
                  </div>
                ) : msg.streaming ? (
                  <>
                    <span class="spinner" style={{ width: 12, height: 12, display: 'inline-block', marginLeft: 8, borderWidth: 2, verticalAlign: 'middle' }} />
                  </>
                ) : null}
              </div>
            </div>
            {msg.timestamp && !msg.streaming && (
              <div class={`msg-timestamp ${msg.role}`}>
                {msg.role === 'assistant' && msg.model && (
                  <span class={`model-badge model-badge--full ${modelBadge(msg.model, false).cls}`} title={msg.model} onClick={(e: MouseEvent) => openTurnMeta(msg, e)}>{turnModelLabel(msg.model, registryModels)}</span>
                )}
                {msg.role === 'assistant' && msg.costUsd != null && msg.costUsd > 0 && (
                  <span class="cost-badge" title={`$${msg.costUsd.toFixed(6)}`} onClick={(e: MouseEvent) => openTurnMeta(msg, e)}>
                    ${msg.costUsd < 0.01 ? msg.costUsd.toFixed(4) : msg.costUsd.toFixed(2)}
                  </span>
                )}
                {msg.role === 'assistant' && i === lastAssistantIdx && consultationSummary && (
                  <span
                    class="consult-summary-badge"
                    title="Consultations this session"
                    onClick={(e: MouseEvent) => { e.stopPropagation(); setConsultDrawerOpen(true); }}
                  >
                    🧠 {consultationSummary.count}{consultationSummary.totalCost > 0 ? ` · ${fmtCost(consultationSummary.totalCost)}` : ''}
                  </span>
                )}
                {/* Per-message read-aloud ▶ — the "same way" as the markdown viewer
                    (docTtsStore + DocTtsPlaybackBar + paragraph highlight). Shown only
                    for completed assistant messages with a persisted id (the segment
                    walker needs a static DOM, so streaming is excluded — already
                    guaranteed by the outer !msg.streaming). Gated OFF on browser-TTS
                    profiles (Decision 3) — there the chat pill already owns client speech. */}
                {msg.role === 'assistant' && msg.id && !isBrowserTtsProfile && (msg.text || '').trim() && (
                  <button
                    class={`msg-read-aloud-btn${isChatReadActiveForMsg(msg.id) ? ' active' : ''}`}
                    title={isChatReadActiveForMsg(msg.id) ? 'Stop reading' : 'Read aloud'}
                    aria-label={isChatReadActiveForMsg(msg.id) ? 'Stop reading this message' : 'Read this message aloud'}
                    aria-pressed={isChatReadActiveForMsg(msg.id)}
                    onClick={(e: MouseEvent) => { e.stopPropagation(); onReadMessageAloud(msg.id!); }}
                  >
                    {isChatReadActiveForMsg(msg.id)
                      ? (chatTtsPhase === 'loading' ? '…' : '⏹')
                      : '🔊'}
                  </button>
                )}
                {msg.role === 'user' && (
                  <span
                    class={`user-initial-badge${isUserCollapsed ? ' user-initial-badge--collapsed' : ''}`}
                    title={isUserCollapsed ? 'Tap to expand' : 'Tap to collapse'}
                  >{userInitial}</span>
                )}
                {formatMsgTime(msg.timestamp)}
                {msg.role === 'user' && msg.correction?.changed && (
                  <span
                    class="stt-corrected-badge"
                    title={`Transcript corrected via ${msg.correction.provider} (${msg.correction.latencyMs}ms)`}
                  >✨</span>
                )}
                {msg.role === 'user' && (
                  <span class={`user-bubble-collapse-hint ${isUserCollapsed ? 'user-bubble-collapse-hint--closed' : 'user-bubble-collapse-hint--open'}`}>▾</span>
                )}
              </div>
            )}
          </div>
        );
      })}
      {/* PERSISTENT BUSY INDICATOR (Phase 2) — belt and braces. Driven PURELY by server-authoritative
          busy (markServerBusy from QUERY_IN_PROGRESS / SESSIONS_RECONCILE.activeQuerySessionIds /
          MESSAGES_LOADED.isSessionBusy). It renders whenever a turn is running and nothing else is
          moving on screen — no streaming bubble yet, no tool activity — so a LIVE turn can never
          render as a dead one, even if every tool flush was lost. */}
      {busy && !messages.some((m) => m.streaming) && (
        <div class="working-indicator" role="status" aria-live="polite">
          <span class="working-dot" aria-hidden="true" /> Working…
        </div>
      )}

      {/* Live ToolTrace — shown inline during an active query, disappears when CLAUDE_DONE
          persists the activities as a role='tool' message in the history */}
      {busy && toolActivities.length > 0 && (
        <ToolTrace
          text={JSON.stringify(
            toolActivities
              .filter((a) => a.type === 'end' || (a.type === 'start' && !toolActivities.some(
                (b) => b.type === 'end' && b.tool === a.tool && b.turnIndex === a.turnIndex
              )))
              .map((a) => ({ type: a.type, tool: a.tool, input: a.input ?? '' }))
          )}
          live={true}
        />
      )}

      <div ref={bottomRef} />

      {providerError && isAnthropicAuthCard(providerError) ? (
        /* Anthropic auth/credit failure — actionable guidance card, not a raw JSON dump (#1057) */
        <div class="provider-error-banner provider-error-card--auth">
          <span class="provider-error-icon">&#128272;</span>
          <div class="provider-error-card-body">
            <div class="provider-error-card-title">{ANTHROPIC_AUTH_CARD_COPY.title}</div>
            <div class="provider-error-msg">
              The agent could not authenticate with Anthropic. Your dev box needs valid Claude credentials.
            </div>
            <ol class="provider-error-card-steps">
              <li>Open a terminal on your dev box (or the in-app SSH terminal).</li>
              <li>Run <code>claude login</code> and complete the sign-in.</li>
              <li>Send your message again.</li>
            </ol>
          </div>
          <div class="provider-error-actions">
            {onOpenSshTerminal && (
              <button
                type="button"
                class="provider-error-btn provider-error-btn--fallback"
                onClick={onOpenSshTerminal}
              >
                Open SSH terminal
              </button>
            )}
            <button
              type="button"
              class="provider-error-btn provider-error-btn--dismiss"
              onClick={onDismissProviderError}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : providerError && (
        <div class="provider-error-banner">
          <span class="provider-error-icon">&#9888;</span>
          <span class="provider-error-msg">{providerError.message}</span>
          <div class="provider-error-actions">
            {providerError.offerFallback && onRetryWithProvider && lastUserText && (
              <button
                type="button"
                class="provider-error-btn provider-error-btn--fallback"
                onClick={() => onRetryWithProvider(lastUserText, 'anthropic')}
              >
                Try with Claude
              </button>
            )}
            {!providerError.authRequired && onRetryWithProvider && lastUserText && (
              /* Hide the "Retry Gemini" dead-end when the error is a missing Gemini
                 key (authRequired) — re-running the same keyless turn just fails
                 again. The "Try with Claude" fallback above is the working escape.
                 Phase 6a (model-meta-hardening). */
              <button
                type="button"
                class="provider-error-btn provider-error-btn--retry"
                onClick={() => onRetryWithProvider(lastUserText, 'google')}
              >
                Retry Gemini
              </button>
            )}
            <button
              type="button"
              class="provider-error-btn provider-error-btn--dismiss"
              onClick={onDismissProviderError}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Image lightbox is now global — see ImageLightbox.tsx, mounted per-shell in
          MobileShell.tsx AND DesktopShell.tsx (mirrors the CoachLayer precedent) */}
      </div>

      {/* Dots + cluster rows (Bottom-nav r10) -- shown whenever the session has messages;
          not gated on scroll position. Visual order top->bottom: dots row, then the
          chat-scoped cluster, then the composer. Both are position:fixed; the vertical
          anchors live in style.css (.composer-dots-row / .composer-cluster-row). DOM order
          here mirrors the visual order for readability only. */}
      {!selectionMode && (
        <>
          {/* Dots row (r10: now the UPPER row) -- session dots only. Persistent
              wayfinding; not gated on scroll position. */}
          {sessionDots && (
            <div class="composer-dots-row">
              <div class="ctb-dots-zone">{sessionDots}</div>
            </div>
          )}
          {/* Cluster row (r10: now the LOWER row, flush on the composer) -- the three
              chat-scoped controls: context (% + 1M window), playback transport, and find,
              grouped into ONE centered cluster directly BELOW the dots row. Find is
              rendered as a pill only when there's something to search. */}
          {(clusterContext || playbackTransport || onToggleFind || clusterBrowser || clusterDevPreview || clusterWorkflow || clusterSsh || clusterGear || (readAloudCollapsed && onShowReadAloudBar)) && (
            <div class="composer-cluster-row">
              {/* r17 + agent-runs §4: right-aligned over the mic. Order (l→r): browser ·
                  dev-preview · runs · ssh · gear · find · context · playback. Read from the
                  right edge: playback (+ speed, over the mic) · context · find · gear · ssh ·
                  runs · dev-preview · browser. */}
              {clusterBrowser && (
                <div class="ccl-action-zone">{clusterBrowser}</div>
              )}
              {clusterDevPreview && (
                <div class="ccl-action-zone">{clusterDevPreview}</div>
              )}
              {clusterWorkflow && (
                <div class="ccl-action-zone">{clusterWorkflow}</div>
              )}
              {clusterSsh && (
                <div class="ccl-action-zone">{clusterSsh}</div>
              )}
              {clusterGear && (
                <div class="ccl-action-zone">{clusterGear}</div>
              )}
              {onToggleFind && !findActive && messages.length > 0 && (
                <button
                  type="button"
                  class="ccl-icon-btn"
                  onClick={onToggleFind}
                  title="Find in conversation"
                  aria-label="Find in conversation"
                >
                  {'\u{1F50D}'}
                </button>
              )}
              {clusterContext && (
                <div class="ccl-context-zone">{clusterContext}</div>
              )}
              {/* Phase 5: re-show chip for a collapsed composer-slot read-aloud bar.
                  Load-bearing — the slot bar replaces the input row + mic, so this is
                  the way back to the controls (and to free the input) while narration
                  continues. Only mobile passes these props (desktop keeps its floating
                  bar), so the chip is naturally absent on desktop. */}
              {readAloudCollapsed && onShowReadAloudBar && (
                <button
                  type="button"
                  class="ccl-icon-btn ccl-readaloud-reshow active"
                  onClick={onShowReadAloudBar}
                  title="Show read-aloud controls"
                  aria-label="Show read-aloud controls"
                >
                  {'🔊'}
                </button>
              )}
              {playbackTransport && (
                <div class="ccl-transport-zone">{playbackTransport}</div>
              )}
            </div>
          )}
        </>
      )}
      </div>
      <TurnMetaDrawer
        open={turnMetaOpen}
        onClose={() => setTurnMetaOpen(false)}
        messageId={turnMetaMessageId}
        anchorY={turnMetaAnchorY}
        initialData={turnMetaInitial}
      />

      {/* Read-aloud playback bar — reused verbatim from the doc viewer (store-only
          renderer). Mounted only when a chat-message read is active in THIS session;
          DocTtsPlaybackBar self-hides when phase is idle/stopped. Phase 5: mobile
          suppresses this floating mount (the bar moves into the composer pill slot);
          desktop keeps it until its fast-follow. */}
      {!suppressFloatingReadAloudBar && isChatReadActive && <DocTtsPlaybackBar onClose={ttsStopReading} />}

      {/* Consultation detail drawer */}
      {consultDrawerOpen && consultationSummary && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 9998, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setConsultDrawerOpen(false)}
        >
          <div
            style={{ background: 'var(--bg-card, #1e1e2e)', border: '1px solid var(--border, #313244)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 520, maxHeight: '80svh', overflowY: 'auto', padding: '20px 16px 28px', color: 'var(--fg, #cdd6f4)' }}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: '1rem' }}>🧠 {consultationSummary.count} Consultation{consultationSummary.count !== 1 ? 's' : ''}{consultationSummary.totalCost > 0 ? ` · ${fmtCost(consultationSummary.totalCost)} total` : ''}</span>
              <button onClick={() => setConsultDrawerOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--fg-muted, #a6adc8)', fontSize: '1.2rem', cursor: 'pointer', padding: '2px 6px' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {consultationSummary.items.map((c, idx) => {
                const p = c.provider || ((c.text || '').startsWith('🧠') ? 'opus' : (c.text || '').startsWith('🔮') ? 'gemini' : (c.text || '').startsWith('⚡') ? 'haiku' : 'ollama');
                const emoji = p === 'opus' ? '🧠' : p === 'gemini' ? '🔮' : p === 'haiku' ? '⚡' : '🦙';
                const label = consultationDisplayLabel(c.model, p);
                const inTok = fmtTokens(c.inputTokens);
                const outTok = fmtTokens(c.outputTokens);
                const cost = fmtCost(c.costUsd);
                return (
                  <div key={idx} style={{ background: 'var(--bg-surface, #181825)', border: '1px solid var(--border, #313244)', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span>{emoji}</span>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
                      {c.model && <span style={{ fontSize: '0.7rem', color: 'var(--fg-muted, #a6adc8)', background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: 4 }}>{c.model}</span>}
                      {c.timestamp && <span style={{ fontSize: '0.72rem', color: 'var(--fg-muted, #a6adc8)', marginLeft: 'auto' }}>{formatMsgTime(c.timestamp)}</span>}
                    </div>
                    {(inTok || outTok || cost) && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--fg-muted, #a6adc8)' }}>
                        {[inTok && `${inTok} in`, outTok && `${outTok} out`, cost].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
